/**
 * Wildberries FBS unit economics, ruleset WB-FBS-2026-v4 (temporary rules
 * for orders received from 2026-08-07).
 *
 * The workbook defines the calculation structure. Mutable WB tariffs are kept
 * explicitly in this pure calculator so they can be replaced and verified
 * without spreading formulas through the table UI.
 *
 * Project decisions:
 * - the first 500 FBS items per zone/day handed to a pickup point cost 10 RUB;
 *   additional items cost 50 RUB;
 * - seller first-mile delivery costs 0 ₽;
 * - FBS KTR is fixed at 1 and is not an input;
 * - WB-funded СПП affects only the displayed buyer price;
 * - manual buyout percent estimates delivery costs caused by non-buyouts;
 * - WB's 2026-08-07 temporary dispatch thresholds are applied to dispatchHours.
 */

export const WB_2026_RULESET = {
  id: "wb-fbs-2026-v4",
  label: "WB FBS 2026 · версия 4 (с 07.08.2026)",
  source: "Unit-экономика WB.xlsx · Unit по артикулам WB FBS",
  logisticsUpTo200Ml: 23,
  logisticsUpTo400Ml: 26,
  logisticsUpTo600Ml: 29,
  logisticsUpTo800Ml: 30,
  logisticsUpToOneLiter: 32,
  logisticsFirstLiter: 46,
  logisticsAdditionalLiter: 14,
  fbsAcceptancePerItem: 10,
  fbsAcceptanceThreshold: 500,
  fbsAcceptanceAfterThreshold: 50,
  fbsAutoReturnFirstLiter: 25,
  fbsAutoReturnAdditionalLiter: 4,
};

export const WB_2026_DEFAULT_INPUTS = {
  price: 0,
  packaging: 0,
  overhead: 0,
  widthCm: 10,
  lengthCm: 6,
  heightCm: 11,
  buyoutPercent: 80,
  warehouseCoefficientPercent: 100,
  categoryCommissionPercent: 0,
  acquiringPercent: 3,
  sppPercent: 35,
  dispatchHours: 24,
  taxMode: "usn6",
  advertisingPercent: 0,
};

export const WB_TAX_MODES = [
  { value: "usn6", label: "УСН Доходы 6% · без НДС" },
  { value: "usn6_vat5", label: "УСН Доходы 6% · НДС 5%" },
  { value: "usn6_vat7", label: "УСН Доходы 6% · НДС 7%" },
  { value: "ausn8", label: "АУСН Доходы 8% · без НДС" },
  { value: "ausn8_vat5", label: "АУСН Доходы 8% · НДС 5%" },
  { value: "ausn8_vat7", label: "АУСН Доходы 8% · НДС 7%" },
];

const numberOr = (value, fallback = 0) => {
  const parsed = typeof value === "string" ? Number(value.replace(",", ".")) : Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const nonNegative = (value, fallback = 0) => Math.max(0, numberOr(value, fallback));
const percent = (value, fallback = 0) => Math.min(100, nonNegative(value, fallback));
// Per-sale economics is undefined at 0% buyout. The UI rejects zero; this
// lower bound also keeps legacy records conservative instead of hiding costs.
const buyoutPercent = (value, fallback) =>
  Math.min(100, Math.max(1, numberOr(value, fallback)));
const divide = (value, divisor) => (divisor ? value / divisor : 0);

export function normalizeWb2026Inputs(value = {}) {
  return {
    price: nonNegative(value.price),
    packaging: nonNegative(value.packaging),
    overhead: nonNegative(value.overhead),
    widthCm: nonNegative(value.widthCm, WB_2026_DEFAULT_INPUTS.widthCm),
    lengthCm: nonNegative(value.lengthCm, WB_2026_DEFAULT_INPUTS.lengthCm),
    heightCm: nonNegative(value.heightCm, WB_2026_DEFAULT_INPUTS.heightCm),
    buyoutPercent: buyoutPercent(value.buyoutPercent, WB_2026_DEFAULT_INPUTS.buyoutPercent),
    warehouseCoefficientPercent: nonNegative(
      value.warehouseCoefficientPercent,
      WB_2026_DEFAULT_INPUTS.warehouseCoefficientPercent,
    ),
    categoryCommissionPercent: percent(value.categoryCommissionPercent),
    acquiringPercent: percent(value.acquiringPercent, WB_2026_DEFAULT_INPUTS.acquiringPercent),
    sppPercent: percent(value.sppPercent, WB_2026_DEFAULT_INPUTS.sppPercent),
    dispatchHours: nonNegative(value.dispatchHours, WB_2026_DEFAULT_INPUTS.dispatchHours),
    advertisingPercent: percent(value.advertisingPercent),
    taxMode: WB_TAX_MODES.some((mode) => mode.value === value.taxMode)
      ? value.taxMode
      : WB_2026_DEFAULT_INPUTS.taxMode,
  };
}

function logisticsTariff(volumeLiters) {
  if (volumeLiters <= 0.2) return WB_2026_RULESET.logisticsUpTo200Ml;
  if (volumeLiters <= 0.4) return WB_2026_RULESET.logisticsUpTo400Ml;
  if (volumeLiters <= 0.6) return WB_2026_RULESET.logisticsUpTo600Ml;
  if (volumeLiters <= 0.8) return WB_2026_RULESET.logisticsUpTo800Ml;
  if (volumeLiters <= 1) return WB_2026_RULESET.logisticsUpToOneLiter;
  return (
    WB_2026_RULESET.logisticsFirstLiter +
    (volumeLiters - 1) * WB_2026_RULESET.logisticsAdditionalLiter
  );
}

function dispatchCommission(price, dispatchHours) {
  if (dispatchHours <= 13) {
    return { rate: -5, amount: price * -0.05 };
  }
  if (dispatchHours <= 42) {
    return { rate: -3.5, amount: price * -0.035 };
  }
  if (dispatchHours <= 48) {
    return { rate: 0, amount: 0 };
  }

  // Temporary WB rule: the selected rate applies to every hour elapsed from
  // hour 48 (the wording is "starting from the 48th hour").
  const rate = dispatchHours <= 54 ? 0.3 : dispatchHours <= 60 ? 0.35 : 0.45;
  const amountPercent = (dispatchHours - 48) * rate;
  return {
    rate,
    amount: price * (amountPercent / 100),
  };
}

function taxRates(mode) {
  return {
    income: mode.startsWith("ausn8") ? 0.08 : 0.06,
    vat: mode.endsWith("vat5") ? 0.05 : mode.endsWith("vat7") ? 0.07 : 0,
  };
}

export function calculateWb2026(rawInputs = {}, rawCostPrice = 0) {
  const inputs = normalizeWb2026Inputs(rawInputs);
  const costPrice = nonNegative(rawCostPrice);
  // costPrice is the complete unit cost and already includes packaging.
  // Keep the legacy packaging input in normalized records for compatibility,
  // but do not add it again to the calculation.
  const totalCost = costPrice + inputs.overhead;
  const volumeLiters = (inputs.widthCm * inputs.lengthCm * inputs.heightCm) / 1000;
  const baseLogisticsTariff = logisticsTariff(volumeLiters);
  const warehouseCoefficient = inputs.warehouseCoefficientPercent / 100;
  const buyoutRate = inputs.buyoutPercent / 100;

  // WB charges forward delivery for every order and return delivery for each
  // non-buyout. Normalize both to one successfully sold item. The return tariff
  // is not multiplied by the warehouse coefficient.
  const forwardLogistics = baseLogisticsTariff * warehouseCoefficient;
  const expectedNonBuyoutLogistics = buyoutRate
    ? ((1 - buyoutRate) * baseLogisticsTariff) / buyoutRate
    : 0;
  const logistics = buyoutRate
    ? forwardLogistics / buyoutRate + expectedNonBuyoutLogistics
    : 0;

  const paidAcceptance = WB_2026_RULESET.fbsAcceptancePerItem;
  const commission = inputs.price * (inputs.categoryCommissionPercent / 100);
  const acquiring = inputs.price * (inputs.acquiringPercent / 100);
  const dispatch = dispatchCommission(inputs.price, inputs.dispatchHours);
  const { income: incomeTaxRate, vat: vatRate } = taxRates(inputs.taxMode);
  const vat = inputs.price * vatRate;
  const incomeTax = (inputs.price - vat) * incomeTaxRate;
  const tax = vat + incomeTax;
  const advertisingSpend = inputs.price * (inputs.advertisingPercent / 100);

  // WB-funded СПП is informational. All deductions use the seller price.
  const buyerPrice = inputs.price * (1 - inputs.sppPercent / 100);
  const payoutBeforeTax =
    inputs.price -
    commission -
    acquiring -
    paidAcceptance -
    logistics -
    dispatch.amount;
  const profit = payoutBeforeTax - tax - totalCost;
  const profitAfterAdvertising = profit - advertisingSpend;

  return {
    rulesetId: WB_2026_RULESET.id,
    totalCost,
    volumeLiters,
    logisticsTariff: baseLogisticsTariff,
    forwardLogistics,
    expectedNonBuyoutLogistics,
    logistics,
    paidAcceptance,
    commission,
    acquiring,
    dispatchCommissionRate: dispatch.rate,
    dispatchCommission: dispatch.amount,
    buyerPrice,
    priceAfterSpp: buyerPrice,
    vat,
    incomeTax,
    tax,
    payoutBeforeTax,
    netRevenue: payoutBeforeTax - tax,
    profit,
    margin: divide(profit, inputs.price),
    profitability: divide(profitAfterAdvertising, totalCost),
    advertisingSpend,
    profitAfterAdvertising,
    marginAfterAdvertising: divide(profitAfterAdvertising, inputs.price),
  };
}
